import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import {
  CBadge,
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormSelect,
  CLoadingButton,
  CRow,
  CSmartPagination,
  CSmartTable,
  CWidgetStatsA,
} from "@coreui/react-pro";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import config from "../config/config";
import AdminShell from "../components/admin/AdminShell";
import { useAppearance } from "../context/AppearanceContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { buildAuthHeaders, clearStoredAuth, getStoredIdentity } from "../utils/authSession";

const REFRESH_OPTIONS = [5, 10, 15, 30, 60, 300];

const EMPTY = {
  generatedAt: "",
  summary: { suspicious24h: 0, blocked24h: 0, activeBans: 0, totalBans: 0, securityEvents: 0 },
  current: {
    requests15m: 0,
    uniqueTrafficIps15m: 0,
    suspicious15m: 0,
    blocked15m: 0,
    statusLabel: "Ruhig",
    hottestTarget: null,
    hottestPathNow: null,
    lastRequestAt: null,
    lastSeenAt: null,
    lastBlockedAt: null,
  },
  history: { bans7d: 0, securityEvents7d: 0, lastBanAt: null },
  timeline: { labels: [], suspicious: [], blocked: [] },
  topIps: [],
  topTargets: [],
  topTags: [],
  feed: [],
  fail2ban: { active: [], recent: [] },
  securityEvents: { recent: [] },
  sources: {
    debugLogPath: "",
    debugLogActive: false,
    debugLogReadable: false,
    debugLogWritable: false,
    debugLogUpdatedAt: null,
    debugLogIssue: null,
    fail2banLogPath: "",
    fail2banLogActive: false,
    fail2banLogReadable: false,
    fail2banLogUpdatedAt: null,
    fail2banLogIssue: null,
    fail2banClientActive: false,
    fail2banClientIssue: null,
  },
};

function normalizeOverviewPayload(payload = {}) {
  const rawSources = payload?.sources || {};
  const next = {
    ...EMPTY,
    ...(payload || {}),
    summary: { ...EMPTY.summary, ...(payload?.summary || {}) },
    current: { ...EMPTY.current, ...(payload?.current || {}) },
    history: { ...EMPTY.history, ...(payload?.history || {}) },
    timeline: { ...EMPTY.timeline, ...(payload?.timeline || {}) },
    fail2ban: { ...EMPTY.fail2ban, ...(payload?.fail2ban || {}) },
    securityEvents: { ...EMPTY.securityEvents, ...(payload?.securityEvents || {}) },
    sources: { ...EMPTY.sources, ...(payload?.sources || {}) },
  };

  if (typeof rawSources.debugLogActive !== "boolean") {
    next.sources.debugLogActive = Boolean(
      next.sources.debugLogReadable ||
      next.sources.debugFeedCount ||
      next.feed.length ||
      next.current.requests15m
    );
  }

  if (typeof rawSources.debugLogReadable !== "boolean") {
    next.sources.debugLogReadable = Boolean(
      next.sources.debugLogActive ||
      next.sources.debugFeedCount ||
      next.feed.length
    );
  }

  if (typeof rawSources.fail2banClientActive !== "boolean") {
    next.sources.fail2banClientActive = Boolean(
      next.summary.activeBans ||
      next.fail2ban.active.length ||
      next.fail2ban.recent.length
    );
  }

  if (typeof rawSources.fail2banLogReadable !== "boolean") {
    next.sources.fail2banLogReadable = Boolean(
      next.sources.fail2banLogActive ||
      next.summary.activeBans ||
      next.fail2ban.active.length ||
      next.fail2ban.recent.length ||
      next.history.bans7d
    );
  }

  if (typeof rawSources.fail2banLogActive !== "boolean") {
    next.sources.fail2banLogActive = next.sources.fail2banLogReadable;
  }

  return next;
}

function fmt(value) {
  if (!value) return "Noch nichts erfasst";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unklar" : date.toLocaleString("de-DE", { hour12: false });
}

function compactIssue(value) {
  if (!value) return "";
  return String(value).split("\n")[0].trim();
}

function buildFeed(feed) {
  return [...(feed || [])]
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
    .slice(0, 30)
    .map((entry, index) => ({
      id: `${entry.requestId || index}-${entry.timestamp || index}`,
      time: fmt(entry.timestamp),
      ip: entry.ip || "-",
      path: entry.url || "-",
      type: entry.suspicious ? entry.label || entry.tag || "Treffer" : entry.method || "REQ",
      status: entry.status ?? "-",
      tone: entry.suspicious || Number(entry.status || 0) >= 400 ? "danger" : "normal",
    }));
}

function buildHistory(overview) {
  const bans = (overview.fail2ban.recent || []).map((entry, index) => ({
    id: `ban-${index}-${entry.ip || ""}`,
    timestamp: entry.timestamp,
    time: fmt(entry.timestamp),
    source: "Fail2Ban",
    type: entry.action || "Ban",
    ip: entry.ip || "-",
    target: entry.jail || "-",
    status: entry.country || "-",
    tone: entry.action === "Ban" ? "danger" : "normal",
  }));
  const events = (overview.securityEvents.recent || []).map((entry, index) => ({
    id: `evt-${index}-${entry.ip || ""}`,
    timestamp: entry.timestamp,
    time: fmt(entry.timestamp),
    source: "Security Event",
    type: entry.tag || "Event",
    ip: entry.ip || "-",
    target: entry.url || "-",
    status: entry.status ?? "-",
    tone: Number(entry.status || 0) >= 400 ? "danger" : "normal",
  }));
  return [...bans, ...events]
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
    .slice(0, 30);
}

function valueStyle(tone, palette) {
  if (tone === "danger") return { color: palette.danger };
  if (tone === "success") return { color: palette.success };
  if (tone === "warning") return { color: palette.accent };
  return { color: palette.primary };
}

function chartData(overview, palette) {
  return {
    labels: overview.timeline.labels || [],
    datasets: [
      {
        label: "Auffaellig",
        data: overview.timeline.suspicious || [],
        borderColor: palette.primary,
        backgroundColor: palette.primaryFill,
        pointRadius: 2,
        borderWidth: 2,
        tension: 0.3,
        fill: true,
      },
      {
        label: "Geblockt",
        data: overview.timeline.blocked || [],
        borderColor: palette.danger,
        backgroundColor: palette.dangerFill,
        pointRadius: 2,
        borderWidth: 2,
        tension: 0.25,
        fill: true,
      },
    ],
  };
}

function chartOptions(palette) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: palette.muted,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: palette.muted },
        grid: { color: palette.grid },
      },
      y: {
        ticks: { color: palette.muted },
        grid: { color: palette.grid },
      },
    },
  };
}

function getPalette(mode) {
  if (mode === "night") {
    return {
      pageGlow: "radial-gradient(circle at top right, rgba(37,99,255,.28), transparent 38%), radial-gradient(circle at bottom left, rgba(20,184,166,.14), transparent 34%), #08111f",
      rail: "#0b1830",
      card: "#0d1b31",
      cardAlt: "#10203a",
      hero: "linear-gradient(135deg, rgba(11, 32, 71, 0.98), rgba(9, 20, 45, 0.95) 58%, rgba(6, 27, 65, 0.98))",
      text: "#f5f8ff",
      muted: "#9aacc7",
      border: "rgba(88, 126, 190, 0.35)",
      primary: "#3b82f6",
      primaryFill: "rgba(59, 130, 246, 0.18)",
      accent: "#14b8a6",
      danger: "#ef476f",
      dangerFill: "rgba(239, 71, 111, 0.12)",
      success: "#16a34a",
      successFill: "rgba(22, 163, 74, 0.16)",
      grid: "rgba(128, 157, 197, 0.16)",
      shadow: "0 24px 60px rgba(3, 10, 24, 0.42)",
    };
  }
  return {
    pageGlow: "radial-gradient(circle at top right, rgba(14, 165, 233, 0.13), transparent 32%), radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.08), transparent 34%), #edf4fb",
    rail: "#f8fbff",
    card: "#ffffff",
    cardAlt: "#f7fbff",
    hero: "linear-gradient(135deg, rgba(248, 251, 255, 0.98), rgba(236, 245, 255, 0.98) 60%, rgba(221, 241, 255, 0.98))",
    text: "#17243a",
    muted: "#62738d",
    border: "rgba(84, 123, 198, 0.20)",
    primary: "#2563ff",
    primaryFill: "rgba(37, 99, 255, 0.14)",
    accent: "#06b6d4",
    danger: "#d74a6a",
    dangerFill: "rgba(215, 74, 106, 0.12)",
    success: "#2aa36b",
    successFill: "rgba(42, 163, 107, 0.14)",
    grid: "rgba(149, 170, 205, 0.22)",
    shadow: "0 24px 50px rgba(27, 55, 96, 0.12)",
  };
}

function summaryCards(overview) {
  return [
    { label: "Requests jetzt", value: overview.current.requests15m, note: `${overview.current.uniqueTrafficIps15m} Quellen`, tone: "primary" },
    { label: "Auffaellig jetzt", value: overview.current.suspicious15m, note: "letzte 15 Minuten", tone: "warning" },
    { label: "Geblockt jetzt", value: overview.current.blocked15m, note: "direkt abgewehrt", tone: "success" },
    { label: "Aktive Sperren", value: overview.summary.activeBans, note: `gesamt ${overview.summary.totalBans}`, tone: "danger" },
    { label: "Auffaellig 24h", value: overview.summary.suspicious24h, note: "historische Einordnung", tone: "neutral" },
  ];
}

function widgetColorForTone(tone) {
  if (tone === "warning") return "warning";
  if (tone === "success") return "success";
  if (tone === "danger") return "danger";
  if (tone === "neutral") return "info";
  return "primary";
}

const FEED_COLUMNS = [
  { key: "time", label: "Zeit" },
  { key: "ip", label: "IP" },
  { key: "path", label: "Pfad" },
  { key: "type", label: "Typ" },
  { key: "status", label: "Status" },
  { key: "detail", label: "Detail", sorter: false, filter: false },
];

const HISTORY_COLUMNS = [
  { key: "time", label: "Zeit" },
  { key: "source", label: "Quelle" },
  { key: "type", label: "Typ" },
  { key: "ip", label: "IP" },
  { key: "target", label: "Ziel / Detail" },
  { key: "status", label: "Status" },
  { key: "detail", label: "Detail", sorter: false, filter: false },
];

export default function SecurityDashboard() {
  const navigate = useNavigate();
  const sessionUserid = getStoredIdentity().userid;
  const authHeaders = useMemo(() => buildAuthHeaders(), []);
  const { resolvedMode } = useAppearance();
  const palette = useMemo(() => getPalette(resolvedMode), [resolvedMode]);
  const [overview, setOverview] = useState(EMPTY);
  const [refreshSeconds, setRefreshSeconds] = useState(15);
  const [state, setState] = useState({ loading: true, refreshing: false, error: "" });
  const [refreshReadback, setRefreshReadback] = useState("");
  const [accessState, setAccessState] = useState({ open: false, title: "", message: "", actionLabel: "", actionHref: "/login" });
  const [feedPage, setFeedPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedFeedId, setSelectedFeedId] = useState("");
  const [selectedHistoryId, setSelectedHistoryId] = useState("");

  const loadOverview = async (refreshing = false) => {
    setState((current) => ({ ...current, loading: !refreshing, refreshing, error: "" }));
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/security-overview`, {
        headers: authHeaders,
        params: { t: Date.now() },
      });
        const nextOverview = normalizeOverviewPayload(res.data || {});
      setOverview(nextOverview);
      setRefreshReadback(
        refreshing
          ? `Security-Stand aktualisiert. Neuer Bezugszeitpunkt: ${fmt(nextOverview.generatedAt)}.`
          : `Security-Stand geladen. Bezugszeitpunkt: ${fmt(nextOverview.generatedAt)}.`,
      );
      setState({ loading: false, refreshing: false, error: "" });
    } catch (error) {
      const status = Number(error?.response?.status || error?.status || 0);
      if (status === 401) {
        clearStoredAuth();
        setAccessState({
          open: true,
          title: "Sitzung abgelaufen",
          message: "Deine Admin-Sitzung ist nicht mehr gueltig. Bitte melde dich erneut an.",
          actionLabel: "Zur Anmeldung",
          actionHref: "/login",
        });
      } else if (status === 403) {
        setAccessState({
          open: true,
          title: "Kein Zugriff",
          message: "Dieses Security-Board ist nur fuer Admin-Zugaenge freigeschaltet.",
          actionLabel: "Zum Admin-Login",
          actionHref: "/login",
        });
      }
      setOverview(EMPTY);
      setRefreshReadback("");
      setState({ loading: false, refreshing: false, error: status ? `Security-API Fehler ${status}` : "Die Security-Daten konnten nicht geladen werden." });
    }
  };

  useEffect(() => {
    document.title = "Experts4Europe | Security Board";
    loadOverview(false);
  }, []);

  useEffect(() => {
    if (!sessionUserid || accessState.open) return undefined;
    const id = window.setInterval(() => loadOverview(true), refreshSeconds * 1000);
    return () => window.clearInterval(id);
  }, [refreshSeconds, sessionUserid, accessState.open]);

  const currentFeed = buildFeed(overview.feed);
  const historyRows = buildHistory(overview);
  const feedPageSize = 10;
  const historyPageSize = 8;
  const feedPageCount = Math.max(1, Math.ceil(currentFeed.length / feedPageSize));
  const historyPageCount = Math.max(1, Math.ceil(historyRows.length / historyPageSize));
  const pagedFeed = currentFeed.slice((feedPage - 1) * feedPageSize, feedPage * feedPageSize);
  const pagedHistory = historyRows.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize);
  const activeFeedEntry = currentFeed.find((entry) => String(entry.id) === String(selectedFeedId)) || pagedFeed[0] || currentFeed[0] || null;
  const activeHistoryEntry = historyRows.find((entry) => String(entry.id) === String(selectedHistoryId)) || pagedHistory[0] || historyRows[0] || null;
  const chart = chartData(overview, palette);
  const chartCfg = chartOptions(palette);
  const cards = summaryCards(overview);
  const debugSourceStatus = overview.sources.debugLogActive ? "Aktiv" : "Unavailable";
  const fail2banSourceStatus = overview.sources.fail2banClientActive && overview.sources.fail2banLogReadable
    ? "Aktiv"
    : overview.sources.fail2banClientActive || overview.sources.fail2banLogReadable
      ? "Teilweise"
      : "Unavailable";
  const debugSourceTone = overview.sources.debugLogActive ? "success" : "danger";
  const fail2banSourceTone = fail2banSourceStatus === "Aktiv"
    ? "success"
    : fail2banSourceStatus === "Teilweise"
      ? "warning"
      : "danger";
  const debugSourceMeta = overview.sources.debugLogIssue
    ? `Problem: ${compactIssue(overview.sources.debugLogIssue)}`
    : overview.sources.debugLogUpdatedAt
      ? `Zuletzt ${fmt(overview.sources.debugLogUpdatedAt)}`
      : "Noch kein Dateistand erfasst";
  const fail2banSourceMeta = overview.sources.fail2banClientIssue
    ? `Client: ${compactIssue(overview.sources.fail2banClientIssue)}`
    : overview.sources.fail2banLogIssue
      ? `Log: ${compactIssue(overview.sources.fail2banLogIssue)}`
      : overview.sources.fail2banLogUpdatedAt
        ? `Log zuletzt ${fmt(overview.sources.fail2banLogUpdatedAt)}`
        : "Noch kein Logstand erfasst";
  const hottestTrafficPath = overview.current.hottestPathNow?.path || null;
  const hottestSecurityLabel =
    overview.current.hottestTarget?.label ||
    overview.current.hottestTarget?.path ||
    null;
  const panelBase = {
    background: palette.card,
    border: `1px solid ${palette.border}`,
    borderRadius: "1rem",
    boxShadow: palette.shadow,
  };
  const softPanel = {
    ...panelBase,
    background: palette.cardAlt,
    boxShadow: "none",
  };
  const metricHeight = { minHeight: 108 };
  const scrollBox = {
    maxHeight: 250,
    overflowY: "auto",
    scrollbarWidth: "thin",
  };

  useEffect(() => {
    setFeedPage((current) => Math.min(current, feedPageCount));
  }, [feedPageCount]);

  useEffect(() => {
    setHistoryPage((current) => Math.min(current, historyPageCount));
  }, [historyPageCount]);

  useEffect(() => {
    if (!currentFeed.length) {
      setSelectedFeedId("");
      return;
    }
    if (!currentFeed.some((entry) => String(entry.id) === String(selectedFeedId))) {
      setSelectedFeedId(String((pagedFeed[0] || currentFeed[0]).id));
    }
  }, [currentFeed, pagedFeed, selectedFeedId]);

  useEffect(() => {
    if (!historyRows.length) {
      setSelectedHistoryId("");
      return;
    }
    if (!historyRows.some((entry) => String(entry.id) === String(selectedHistoryId))) {
      setSelectedHistoryId(String((pagedHistory[0] || historyRows[0]).id));
    }
  }, [historyRows, pagedHistory, selectedHistoryId]);

  return (
    <>
      <AdminShell
        title="Security Monitor"
        subtitle="Jetzt und Vergangenheit bleiben klar getrennt: Live-Lage, aktive Sperren, Muster und Verlauf in einem operativen Sicherheitsarbeitsraum."
        badge="Security Operations"
        topSignal="Security Europe API"
        topContext="Operational Security"
        modeLabel="Jetzt / Vergangenheit"
        actions={
          <>
            <div style={{ minWidth: 180 }}>
              <CFormSelect value={refreshSeconds} onChange={(e) => setRefreshSeconds(Number(e.target.value))}>
                {REFRESH_OPTIONS.map((seconds) => (
                  <option key={seconds} value={seconds}>{`Auto-Refresh ${seconds}s`}</option>
                ))}
              </CFormSelect>
            </div>
            <CLoadingButton color="primary" variant="outline" loading={state.refreshing} disabledOnLoading onClick={() => loadOverview(true)}>
              <RefreshCw size={16} className="me-2" />
              Aktualisieren
            </CLoadingButton>
            <div className="small text-body-secondary">
              Bezugszeitpunkt: {fmt(overview.generatedAt)}
            </div>
          </>
        }
      >
        {state.error ? (
          <CCallout color="danger" className="m-0">
            <AlertTriangle size={16} />
            <span>{state.error}</span>
          </CCallout>
        ) : null}
        {!state.error && refreshReadback ? (
          <CCallout color={state.refreshing ? "info" : "success"} className="m-0">
            <strong>Refresh Readback</strong>
            <div>{state.refreshing ? "Aktualisierung laeuft. Die gleiche Review-Surface wird gleich mit dem neuesten Stand neu gelesen." : refreshReadback}</div>
            <div className="mt-2">
              Diese Aktion aktualisiert nur die Review-Evidenz fuer Feed, Historie und Kennzahlen. Sie fuehrt keine direkte Sicherheitsmassnahme aus.
            </div>
          </CCallout>
        ) : null}

        <CRow className="g-3">
              {cards.map((card) => (
                <CCol key={card.label} xs={12} md={6} xl={2}>
                  <CWidgetStatsA
                    className="h-100"
                    color={widgetColorForTone(card.tone)}
                    title={card.label}
                    value={<span style={{ fontSize: 36, fontWeight: 700, lineHeight: 1.1 }}>{card.value}</span>}
                    chart={<div style={{ color: palette.muted, padding: "0 1rem 1rem", fontSize: 13 }}>{card.note}</div>}
                    style={{ ...softPanel, ...metricHeight, overflow: "hidden" }}
                  />
                </CCol>
              ))}
        </CRow>

        <CRow className="g-3">
          <CCol xl={8}>
            <CCard style={panelBase}>
              <CCardHeader className="d-flex justify-content-between align-items-start gap-3 bg-transparent border-0 pt-4 px-4">
                <div>
                  <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Jetzt</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6 }}>Was passiert gerade?</div>
                  <div style={{ color: palette.muted, marginTop: 6 }}>
                    {overview.current.suspicious15m > 0
                      ? "Wir sehen aktuell Security-Treffer oder Probeverkehr."
                      : overview.current.requests15m > 0
                        ? "Wir sehen normalen API-Verkehr ohne Security-Treffer."
                        : "Im Moment ist die API ruhig."}
                  </div>
                  <CCallout color={overview.current.suspicious15m > 0 ? "warning" : "info"} className="mt-3 mb-0">
                    <strong>Jetzt richtig lesen</strong>
                    <div>{overview.current.suspicious15m > 0 ? "Dieses Segment ist fuer aktive Gegenmassnahmen und Priorisierung gedacht." : "Dieses Segment dient als Live-Orientierung und nicht als historische Auswertung."}</div>
                  </CCallout>
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 16,
                    background: overview.current.suspicious15m > 0 ? palette.dangerFill : palette.successFill,
                    border: `1px solid ${overview.current.suspicious15m > 0 ? palette.danger : palette.success}`,
                    minWidth: 180,
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 18 }}>{overview.current.suspicious15m > 0 ? "Auffaellig" : "Ruhig"}</div>
                  <div style={{ color: palette.muted, marginTop: 4 }}>{`Stand ${fmt(overview.generatedAt)}`}</div>
                </div>
              </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    <CRow className="g-3 mb-3">
                      <CCol md={4}>
                        <CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Letzter Request</div><div style={{ fontWeight: 700, fontSize: 20, marginTop: 10 }}>{fmt(overview.current.lastRequestAt)}</div></CCardBody></CCard>
                      </CCol>
                      <CCol md={4}>
                        <CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Letzter Security-Treffer</div><div style={{ fontWeight: 700, fontSize: 20, marginTop: 10 }}>{fmt(overview.current.lastSeenAt)}</div></CCardBody></CCard>
                      </CCol>
                      <CCol md={4}>
                        <CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Letzter Block</div><div style={{ fontWeight: 700, fontSize: 20, marginTop: 10 }}>{fmt(overview.current.lastBlockedAt)}</div></CCardBody></CCard>
                      </CCol>
                    </CRow>
                    <CCard style={{ ...softPanel, minHeight: 164 }}>
                      <CCardBody>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                          <div style={{ maxWidth: 620 }}>
                            <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Live-Prioritaet</div>
                            <div style={{ fontWeight: 700, fontSize: 30, marginTop: 8 }}>{hottestTrafficPath || "Kein heisses Ziel"}</div>
                            <div style={{ color: palette.muted, marginTop: 10, fontSize: 16 }}>
                              {hottestSecurityLabel
                                ? `Das dominante Signal kommt aktuell ueber ${hottestSecurityLabel}.`
                                : "Im Moment liegt kein dominantes Ziel vor. Die Lage bleibt ruhig und ohne heisse Pfade."}
                            </div>
                          </div>
                          <div className="d-flex gap-3 flex-wrap">
                            {[
                              { label: "Traffic 15m", value: overview.current.requests15m },
                              { label: "Quellen jetzt", value: overview.current.uniqueTrafficIps15m },
                              { label: "Security 15m", value: overview.current.suspicious15m },
                            ].map((metric) => (
                              <div
                                key={metric.label}
                                style={{
                                  minWidth: 132,
                                  padding: "14px 16px",
                                  borderRadius: 16,
                                  background: palette.card,
                                  border: `1px solid ${palette.border}`,
                                }}
                              >
                                <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>{metric.label}</div>
                                <div style={{ fontWeight: 700, fontSize: 32, marginTop: 10 }}>{metric.value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCardBody>
                </CCard>
          </CCol>

          <CCol xl={4}>
                <CRow className="g-3">
                  <CCol xs={12}>
                    <CCard style={panelBase}>
                      <CCardHeader className="bg-transparent border-0 pt-4 px-4">
                        <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Aktiv</div>
                        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Aktive Sperren</div>
                      </CCardHeader>
                      <CCardBody className="pt-0 px-4 pb-4">
                        {(overview.fail2ban.active || []).length ? (
                          <div style={scrollBox}>
                            {(overview.fail2ban.active || []).map((entry, index) => (
                              <div key={`${entry.ip}-${index}`} style={{ ...softPanel, padding: 14, marginBottom: 10 }}>
                                <div style={{ fontWeight: 700 }}>{entry.ip}</div>
                                <div style={{ color: palette.muted, marginTop: 4 }}>{entry.jail || "Fail2Ban Jail"}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <CCard style={softPanel}><CCardBody><div style={{ fontWeight: 700 }}>Keine aktive Sperre</div><div style={{ color: palette.muted, marginTop: 6 }}>Aktuell gibt es keine aktiven Sperren.</div></CCardBody></CCard>
                        )}
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol xs={12}>
                    <CCard style={panelBase}>
                      <CCardHeader className="bg-transparent border-0 pt-4 px-4">
                        <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Quellen</div>
                        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Woraus wir lesen</div>
                      </CCardHeader>
                      <CCardBody className="pt-0 px-4 pb-4 d-flex flex-column gap-3">
                        <CCard style={softPanel}>
                          <CCardBody>
                            <div style={{ color: palette.muted }}>Debug Log</div>
                            <div style={{ ...valueStyle(debugSourceTone, palette), fontWeight: 700, marginTop: 4 }}>{debugSourceStatus}</div>
                            <div style={{ color: palette.muted, marginTop: 6 }}>{overview.sources.debugLogPath || "-"}</div>
                            <div style={{ color: palette.muted, marginTop: 6 }}>{debugSourceMeta}</div>
                          </CCardBody>
                        </CCard>
                        <CCard style={softPanel}>
                          <CCardBody>
                            <div style={{ color: palette.muted }}>Fail2Ban Log</div>
                            <div style={{ ...valueStyle(fail2banSourceTone, palette), fontWeight: 700, marginTop: 4 }}>{fail2banSourceStatus}</div>
                            <div style={{ color: palette.muted, marginTop: 6 }}>{overview.sources.fail2banLogPath || "-"}</div>
                            <div style={{ color: palette.muted, marginTop: 6 }}>{fail2banSourceMeta}</div>
                          </CCardBody>
                        </CCard>
                        <CCard style={softPanel}><CCardBody><div style={{ color: palette.muted }}>Security Events</div><div style={{ fontWeight: 700, fontSize: 28, marginTop: 4 }}>{overview.summary.securityEvents}</div><div style={{ color: palette.muted, marginTop: 6 }}>Persistierte Signale in der Datenbank</div></CCardBody></CCard>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
          </CCol>
        </CRow>

        <CRow className="g-3">
          <CCol xl={8}>
                <CCard style={panelBase}>
                  <CCardHeader className="bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Vergangenheit</div>
                      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Angriffsverlauf der letzten Tage</div>
                    </div>
                    <CBadge color="primary" shape="rounded-pill">{`Letzte Sperre ${fmt(overview.history.lastBanAt)}`}</CBadge>
                  </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    <CCallout color="info" className="mb-3">
                      <strong>Vergangenheit bleibt eigene Ebene</strong>
                      <div>Hier werden Muster, Bans und historische Signale verdichtet, ohne die Live-Lage mit Rueckblick zu vermischen.</div>
                    </CCallout>
                    <CRow className="g-3 mb-3">
                      <CCol md={3}><CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Auffaellig 24h</div><div style={{ fontWeight: 700, fontSize: 32, marginTop: 10 }}>{overview.summary.suspicious24h}</div><div style={{ color: palette.muted, marginTop: 8 }}>Auffaellige Requests im Tagesfenster</div></CCardBody></CCard></CCol>
                      <CCol md={3}><CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Geblockt 24h</div><div style={{ fontWeight: 700, fontSize: 32, marginTop: 10 }}>{overview.summary.blocked24h}</div><div style={{ color: palette.muted, marginTop: 8 }}>Geblockt durch Regel oder Status</div></CCardBody></CCard></CCol>
                      <CCol md={3}><CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Bans 7 Tage</div><div style={{ fontWeight: 700, fontSize: 32, marginTop: 10 }}>{overview.history.bans7d}</div><div style={{ color: palette.muted, marginTop: 8 }}>Neue Sperren laut Fail2Ban</div></CCardBody></CCard></CCol>
                      <CCol md={3}><CCard style={softPanel}><CCardBody><div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Events 7 Tage</div><div style={{ fontWeight: 700, fontSize: 32, marginTop: 10 }}>{overview.history.securityEvents7d}</div><div style={{ color: palette.muted, marginTop: 8 }}>Persistierte Security-Signale</div></CCardBody></CCard></CCol>
                    </CRow>
                    <div style={{ height: 300 }}>
                      <Line data={chart} options={chartCfg} />
                    </div>
                  </CCardBody>
                </CCard>
          </CCol>

          <CCol xl={4}>
                <CCard style={{ ...panelBase, height: "100%" }}>
                  <CCardHeader className="bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Muster</div>
                      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Welche Signale dominieren?</div>
                    </div>
                    <CBadge color="primary" shape="rounded-pill">Live sortiert</CBadge>
                  </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    {(overview.topTags || []).length ? (
                      <div style={scrollBox}>
                        {(overview.topTags || []).map((tag) => (
                          <div key={tag.label} style={{ ...softPanel, padding: 14, marginBottom: 10 }}>
                            <div className="d-flex justify-content-between align-items-center gap-3">
                              <div>
                                <div style={{ fontWeight: 700 }}>{tag.label}</div>
                                <div style={{ color: palette.muted, marginTop: 4 }}>{tag.detail || "Aktives Muster"}</div>
                              </div>
                              <div style={{ ...valueStyle("primary", palette), fontWeight: 700, fontSize: 24 }}>{tag.count}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: palette.muted }}>Noch keine dominanten Muster.</div>
                    )}
                  </CCardBody>
                </CCard>
          </CCol>
        </CRow>

        <CRow className="g-3">
          <CCol xl={3} md={6}>
                <CCard style={{ ...panelBase, height: "100%" }}>
                  <CCardHeader className="bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                    <div>
                      <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Top Ziele</div>
                      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Welche Pfade werden gesucht?</div>
                    </div>
                    <CBadge color="primary" shape="rounded-pill">{`${refreshSeconds}s`}</CBadge>
                  </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    <div style={scrollBox}>
                      {(overview.topTargets || []).length ? (
                        (overview.topTargets || []).map((target) => (
                          <div key={target.path} style={{ ...softPanel, padding: 14, marginBottom: 10 }}>
                            <div style={{ fontWeight: 700 }}>{target.path}</div>
                            <div style={{ color: palette.muted, marginTop: 4 }}>{`${target.count} Treffer`}</div>
                          </div>
                        ))
                      ) : (
                        <div style={{ color: palette.muted }}>Bisher keine auffaelligen Zielpfade.</div>
                      )}
                    </div>
                  </CCardBody>
                </CCard>
          </CCol>

          <CCol xl={3} md={6}>
                <CCard style={{ ...panelBase, height: "100%" }}>
                  <CCardHeader className="bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                    <div>
                      <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Top IPs</div>
                      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Wer drueckt gerade und zuletzt?</div>
                    </div>
                    <CBadge color="primary" shape="rounded-pill">{`${refreshSeconds}s`}</CBadge>
                  </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    <div style={scrollBox}>
                      {(overview.topIps || []).length ? (
                        (overview.topIps || []).map((ip) => (
                          <div key={ip.ip} style={{ ...softPanel, padding: 14, marginBottom: 10 }}>
                            <div style={{ fontWeight: 700 }}>{ip.ip}</div>
                            <div style={{ color: palette.muted, marginTop: 4 }}>{`${ip.count} Requests`}</div>
                          </div>
                        ))
                      ) : (
                        <div style={{ color: palette.muted }}>Noch keine aktiven Angriffsquellen erfasst.</div>
                      )}
                    </div>
                  </CCardBody>
                </CCard>
          </CCol>

          <CCol xl={3} md={6}>
                <CCard style={{ ...panelBase, height: "100%" }}>
                  <CCardHeader className="bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                    <div>
                      <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Live Feed</div>
                      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Requests jetzt</div>
                    </div>
                    <CBadge color="primary" shape="rounded-pill">{`letzte ${Math.min(currentFeed.length || 12, 12)}`}</CBadge>
                  </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    <CCallout color="info" className="mb-3">
                      <strong>Live-Feed</strong>
                      <div>Die Liste zeigt nur den aktuellen Request-Strom. Fuer laengere Rueckblicke dient unten die Historie.</div>
                    </CCallout>
                    <div style={scrollBox}>
                      {pagedFeed.length ? (
                        <CSmartTable
                          columns={FEED_COLUMNS}
                          items={pagedFeed}
                          pagination={false}
                          sorter={false}
                          tableProps={{ hover: true, responsive: true, small: true, borderless: true }}
                          scopedColumns={{
                            status: (item) => (
                              <td style={{ color: item.tone === "danger" ? palette.danger : palette.text }}>
                                {item.status}
                              </td>
                            ),
                            detail: (item) => (
                              <td>
                                <CButton
                                  color={String(activeFeedEntry?.id || "") === String(item.id) ? "primary" : "secondary"}
                                  size="sm"
                                  variant={String(activeFeedEntry?.id || "") === String(item.id) ? undefined : "outline"}
                                  onClick={() => setSelectedFeedId(String(item.id))}
                                >
                                  {String(activeFeedEntry?.id || "") === String(item.id) ? "Aktiv" : "Details"}
                                </CButton>
                              </td>
                            ),
                          }}
                        />
                      ) : (
                        <div style={{ color: palette.muted }}>
                          Kein neuer Request im Feed. Im Moment sehen wir im 15-Minuten-Fenster keinen neuen API-Traffic.
                        </div>
                      )}
                    </div>
                    {currentFeed.length > feedPageSize ? (
                      <div className="mt-3 d-flex justify-content-end">
                        <CSmartPagination
                          activePage={feedPage}
                          align="end"
                          dots
                          doubleArrows
                          onActivePageChange={setFeedPage}
                          pages={feedPageCount}
                        />
                      </div>
                    ) : null}
                    {activeFeedEntry ? (
                      <div className="mt-3 rounded-4 border border-light-subtle bg-body-tertiary p-3">
                        <CCallout color={activeFeedEntry.tone === "danger" ? "warning" : "info"} className="mb-3">
                          <strong>Live Review Evidence</strong>
                          <div>
                            Dies ist die Gegenwartsansicht. Nutze sie fuer den naechsten Blick auf aktuelle Muster, nicht fuer laengere Rueckblicke.
                          </div>
                        </CCallout>
                        <CCallout color="info" className="mb-3">
                          <strong>Read-only Review</strong>
                          <div>Diese Detailflaeche verdichtet Evidenz, Guardrails und den naechsten Pruefblick. Eingriffe oder Bans werden hier nicht neu erfunden, damit Gegenwart und Review sauber getrennt bleiben.</div>
                        </CCallout>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Zeitpunkt</div>
                              <div className="fw-semibold mt-2">{activeFeedEntry.time}</div>
                              <div className="text-body-secondary small mt-3 text-uppercase">Quelle / IP</div>
                              <div className="fw-semibold mt-2">{activeFeedEntry.ip}</div>
                            </div>
                          </CCol>
                          <CCol md={6}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Route / Ereignis</div>
                              <div className="fw-semibold mt-2">{activeFeedEntry.path}</div>
                              <div className="text-body-secondary small mt-3 text-uppercase">Typ / Status</div>
                              <div className="fw-semibold mt-2">{activeFeedEntry.type} · {activeFeedEntry.status}</div>
                            </div>
                          </CCol>
                        </CRow>
                        <CRow className="g-3 mt-1">
                          <CCol md={4}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Signal vs Modus</div>
                              <div className="fw-semibold mt-2">{activeFeedEntry.status}</div>
                              <div className="text-body-secondary mt-1">Jetzt-Ebene, nicht Verlaufsebene</div>
                            </div>
                          </CCol>
                          <CCol md={4}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Quelle vs Ziel</div>
                              <div className="fw-semibold mt-2">{activeFeedEntry.ip}</div>
                              <div className="text-body-secondary mt-1">{activeFeedEntry.path}</div>
                            </div>
                          </CCol>
                          <CCol md={4}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Naechster Blick</div>
                              <div className="fw-semibold mt-2">
                                {activeFeedEntry.tone === "danger" ? "Aktuellen Musterpfad priorisieren" : "Nur weiter beobachten"}
                              </div>
                              <div className="text-body-secondary mt-1">Keine direkte Row-Aktion in dieser Detailflaeche</div>
                            </div>
                          </CCol>
                        </CRow>
                      </div>
                    ) : null}
                  </CCardBody>
                </CCard>
          </CCol>

          <CCol xl={3} md={6}>
                <CCard style={{ ...panelBase, height: "100%" }}>
                  <CCardHeader className="bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                    <div>
                      <div style={{ color: palette.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em" }}>Historie</div>
                      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>Events und Bans</div>
                    </div>
                    <CBadge color="primary" shape="rounded-pill">live sortiert</CBadge>
                  </CCardHeader>
                  <CCardBody className="pt-0 px-4 pb-4">
                    <CCallout color="warning" className="mb-3">
                      <strong>Historie</strong>
                      <div>Diese Liste dient der Einordnung von Verlauf und Sperren. Sie ist bewusst getrennt vom Live-Feed.</div>
                    </CCallout>
                    <div style={scrollBox}>
                      {pagedHistory.length ? (
                        <CSmartTable
                          columns={HISTORY_COLUMNS}
                          items={pagedHistory}
                          pagination={false}
                          sorter={false}
                          tableProps={{ hover: true, responsive: true, small: true, borderless: true }}
                          scopedColumns={{
                            status: (item) => (
                              <td style={{ color: item.tone === "danger" ? palette.danger : palette.text }}>
                                {item.status}
                              </td>
                            ),
                            detail: (item) => (
                              <td>
                                <CButton
                                  color={String(activeHistoryEntry?.id || "") === String(item.id) ? "primary" : "secondary"}
                                  size="sm"
                                  variant={String(activeHistoryEntry?.id || "") === String(item.id) ? undefined : "outline"}
                                  onClick={() => setSelectedHistoryId(String(item.id))}
                                >
                                  {String(activeHistoryEntry?.id || "") === String(item.id) ? "Aktiv" : "Details"}
                                </CButton>
                              </td>
                            ),
                          }}
                        />
                      ) : (
                        <div style={{ color: palette.muted }}>
                          Keine Historie sichtbar. Noch keine historischen Security-Eintraege gefunden.
                        </div>
                      )}
                    </div>
                    {historyRows.length > historyPageSize ? (
                      <div className="mt-3 d-flex justify-content-end">
                        <CSmartPagination
                          activePage={historyPage}
                          align="end"
                          dots
                          doubleArrows
                          onActivePageChange={setHistoryPage}
                          pages={historyPageCount}
                        />
                      </div>
                    ) : null}
                    {activeHistoryEntry ? (
                      <div className="mt-3 rounded-4 border border-light-subtle bg-body-tertiary p-3">
                        <CCallout color={activeHistoryEntry.tone === "danger" ? "warning" : "info"} className="mb-3">
                          <strong>History Review Evidence</strong>
                          <div>
                            Diese Detailflaeche ordnet Verlauf, Ban-Kontext und Zielroute ein, ohne die Live-Lage mit Rueckblick zu vermischen.
                          </div>
                        </CCallout>
                        <CCallout color="warning" className="mb-3">
                          <strong>Detail Discipline</strong>
                          <div>Historie bleibt Rueckblick. Diese Flaeche hilft beim Einordnen, Spieglen gegen den Live-Stand und Setzen des naechsten Pruefblicks, nicht beim Ausloesen neuer Security-Aktionen.</div>
                        </CCallout>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Zeitpunkt / Quelle</div>
                              <div className="fw-semibold mt-2">{activeHistoryEntry.time}</div>
                              <div className="text-body-secondary mt-1">{activeHistoryEntry.source}</div>
                              <div className="text-body-secondary small mt-3 text-uppercase">IP / Ziel</div>
                              <div className="fw-semibold mt-2">{activeHistoryEntry.ip}</div>
                              <div className="text-body-secondary mt-1">{activeHistoryEntry.target}</div>
                            </div>
                          </CCol>
                          <CCol md={6}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Typ / Status</div>
                              <div className="fw-semibold mt-2">{activeHistoryEntry.type}</div>
                              <div className="text-body-secondary mt-1">{activeHistoryEntry.status}</div>
                              <div className="text-body-secondary small mt-3 text-uppercase">Naechster Blickpunkt</div>
                              <div className="fw-semibold mt-2">
                                {activeHistoryEntry.source === "Fail2Ban" ? "Ban-Verlauf und Jail-Kontext gegen aktuelle Lage pruefen." : "Signal gegen den aktuellen Feed und bekannte Muster spiegeln."}
                              </div>
                            </div>
                          </CCol>
                        </CRow>
                        <CRow className="g-3 mt-1">
                          <CCol md={4}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Quelle vs Wirkung</div>
                              <div className="fw-semibold mt-2">{activeHistoryEntry.source}</div>
                              <div className="text-body-secondary mt-1">{activeHistoryEntry.status}</div>
                            </div>
                          </CCol>
                          <CCol md={4}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Historie vs Jetzt</div>
                              <div className="fw-semibold mt-2">
                                {activeHistoryEntry.source === "Fail2Ban" ? "Historischer Sperrstand" : "Historisches Signal"}
                              </div>
                              <div className="text-body-secondary mt-1">Immer gegen den oberen Live-Feed spiegeln</div>
                            </div>
                          </CCol>
                          <CCol md={4}>
                            <div className="rounded-4 border bg-body p-3 h-100">
                              <div className="text-body-secondary small text-uppercase">Naechster Blickpunkt</div>
                              <div className="fw-semibold mt-2">
                                {activeHistoryEntry.source === "Fail2Ban" ? "Jail- und Ban-Kontext pruefen" : "Pattern gegen aktuelle Requests spiegeln"}
                              </div>
                              <div className="text-body-secondary mt-1">Keine direkte Intervention aus der Detailflaeche</div>
                            </div>
                          </CCol>
                        </CRow>
                      </div>
                    ) : null}
                  </CCardBody>
                </CCard>
          </CCol>
        </CRow>
      </AdminShell>

      <Dialog open={accessState.open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{accessState.title}</DialogTitle>
            <DialogDescription>{accessState.message}</DialogDescription>
          </DialogHeader>
          <div className="d-flex justify-content-end gap-2">
            <CButton color="secondary" variant="ghost" onClick={() => navigate("/")}>Startseite</CButton>
            <CButton color="primary" onClick={() => navigate(accessState.actionHref)}>{accessState.actionLabel}</CButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
